import { initialize } from 'launchdarkly-js-client-sdk';
import { createHarness, defaultHarnessKeyFromLdKey } from '@pearsontechnology/bronte-lib-harness';
import appConfig from 'appConfig';
import featureKeys from './featureKeys';

/**
 * Builds a service to access FeatureFlags
 */
export default (() => {
  const LD_ROUTING_FLAG_KEY = featureKeys.SERVE_FROM_LAUNCHDARKLY;

  let featureFlags = {};
  let _subscriptions = [];
  let ldclient = null;
  let harnessInstance = null;
  let useHarness = false;
  let isInitialized = false;
  let isInitializedPromiseResolve;
  let lastUserForHarness;

  const isInitializedPromise = new Promise((resolve) => {
    isInitializedPromiseResolve = resolve;
  });
  function warnFeatureFlagResolution(reason, featureName, detail) {
    const suffix = detail ? ` ${detail}` : '';
    /* eslint-disable-next-line no-console */
    console.warn(`[FeatureFlags] ${reason} (featureName=${featureName})${suffix}`);
    return false;
  }
  function shouldServeMappedFlagsFromLaunchDarkly() {
    return featureFlags[LD_ROUTING_FLAG_KEY] === true;
  }
  function isHarnessEnabled() {
    const key = typeof appConfig.getHarnessFMESDKKey === 'function' ? appConfig.getHarnessFMESDKKey() : '';
    return String(key).length > 0;
  }

  function notifyListeners(featureFlagThatChanged, effectiveValue) {
    if (effectiveValue !== undefined) {
      featureFlags[featureFlagThatChanged] = effectiveValue;
    }
    _subscriptions.forEach((item) => {
      if (item.flag === featureFlagThatChanged) {
        item.callback({
          flagName: featureFlagThatChanged,
          value: featureFlags[featureFlagThatChanged],
        });
      }
    });
  }

  function is(featureName) {
    if (!isInitialized) {
      throw new Error(`You tried to check the status of ${featureName} before the service got initialized`);
    }
    if (Object.prototype.hasOwnProperty.call(featureFlags, featureName)) {
      return featureFlags[featureName];
    }
    if (shouldServeMappedFlagsFromLaunchDarkly()) {
      if (ldclient) {
        const ldFlags = ldclient.allFlags();
        if (Object.prototype.hasOwnProperty.call(ldFlags, featureName)) {
          return ldFlags[featureName];
        }
      }
      return warnFeatureFlagResolution('ld_only_unknown', featureName);
    }
    if (featureName === featureKeys.SERVE_FROM_LAUNCHDARKLY) {
      return warnFeatureFlagResolution('routing_key_unknown', featureName);
    }
    const harnessResult = harnessInstance.getHarnessBooleanForLdStyleKey(featureName);

    if (harnessResult === null) {
      return warnFeatureFlagResolution(
        'harness_not_ready',
        featureName,
        'Harness is not available to evaluate; try again after initialization completes'
      );
    }
    if (!harnessResult.inHarness) {
      return warnFeatureFlagResolution('not_in_harness', featureName, 'flag not defined in Split');
    }
    return harnessResult.value;
  }

  function unsubscribe(flag) {
    _subscriptions = _subscriptions.filter((sub) => sub.flag !== flag);
  }

  function getTreatmentConfig(featureName) {
    if (!useHarness || !harnessInstance) {
      return null;
    }
    const factory = harnessInstance.getSplitFactory();
    if (!factory) {
      return null;
    }
    try {
      const harnessKey = defaultHarnessKeyFromLdKey(featureName);
      const { config } = factory.client().getTreatmentWithConfig(harnessKey);
      return config ? JSON.parse(config) : null;
    } catch (e) {
      return null;
    }
  }

  function buildLaunchDarklyUser(user) {
    if (!user) {
      return { anonymous: true };
    }
    return {
      key: user.accountId || '',
      firstName: user.givenName || '',
      lastName: user.familyName || '',
    };
  }

  async function createLaunchDarklyClientAndLoadFlags(user) {
    const ldOptions = {
      evaluationReasons: true,
      bootstrap: 'localstorage',
    };
    const client = initialize(
      appConfig.getLaunchDarklyClientId(),
      buildLaunchDarklyUser(user),
      ldOptions
    );
    await client.waitForInitialization();
    return { client, flags: { ...client.allFlags() } };
  }

  function attachLaunchDarklyChangeListener() {
    if (!ldclient) {
      return;
    }
    ldclient.on('change', handleLaunchDarklyFlagChange);
  }

  function handleLaunchDarklyFlagChange(updatedFlagDetails) {
    const featureFlagKeys = Object.keys(updatedFlagDetails);
    featureFlagKeys.forEach((key) => {
      featureFlags[key] = updatedFlagDetails[key].current;
    });

    const serveKeyFlipped = LD_ROUTING_FLAG_KEY in updatedFlagDetails;
    if (serveKeyFlipped) {
      const nextServeLdOnly = Boolean(featureFlags[LD_ROUTING_FLAG_KEY]);
      const wantDual = isHarnessEnabled() && !nextServeLdOnly;
      if (wantDual !== useHarness) {
        initializeFeatureFlags(lastUserForHarness).catch(() => {});
        return;
      }
    }

    if (useHarness && harnessInstance) {
      harnessInstance.reapplyMappedKeysFromSplit(true, featureFlags);
      featureFlagKeys.forEach((key) => {
        notifyListeners(key);
      });
    } else {
      featureFlagKeys.forEach((key) => {
        notifyListeners(key);
      });
    }
  }

  async function initializeFeatureFlags(user) {
    lastUserForHarness = user === undefined ? null : user;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.userParams) {
      try {
        const parsed = JSON.parse(String(sessionStorage.userParams));
        if (parsed && typeof parsed === 'object' && parsed.personId != null && String(parsed.personId).trim() !== '') {
          lastUserForHarness = {
            accountId: String(parsed.personId).trim(),
          };
        } else {
          // eslint-disable-next-line no-console
          console.error('Unable to fetch user account details/id');
          return {};
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Unable to fetch user account details/id');
        return {};
      }
    }

    if (ldclient) {
      ldclient.close();
      ldclient = null;
    }
    if (harnessInstance) {
      await harnessInstance.teardownHarness();
      harnessInstance = null;
    }
    useHarness = false;

    const { client, flags } = await createLaunchDarklyClientAndLoadFlags(user);
    ldclient = client;
    featureFlags = flags;

    const harnessSdkConfigured = isHarnessEnabled();
    const serveFromLaunchDarklyOnly = Boolean(featureFlags[LD_ROUTING_FLAG_KEY]);
    useHarness = harnessSdkConfigured && !serveFromLaunchDarklyOnly;

    if (useHarness) {
      harnessInstance = createHarness({
        authorizationKey: appConfig.getHarnessFMESDKKey(),
        routingLdKey: LD_ROUTING_FLAG_KEY,
        notifyListeners,
      });
      const harnessSnapshot = await harnessInstance.setupHarness(lastUserForHarness);

      featureFlags = { ...harnessSnapshot };
      harnessInstance.registerConfigurationListenerAfterMerge(featureFlags);
    }

    if (!isInitialized) {
      isInitializedPromiseResolve();
      isInitialized = true;
    }

    _subscriptions.forEach((item) => {
      notifyListeners(item.flag);
    });

    attachLaunchDarklyChangeListener();
    return { ...featureFlags };
  }

  function get() {
    if (!isInitialized) {
      throw new Error(
        'You tried to get the feature flags before the feature flag service got initialized'
      );
    }
    return { ...featureFlags };
  }

  async function getWhenReady() {
    await isInitializedPromise;
    return { ...featureFlags };
  }

  function set(data) {
    const { name, isSet } = data;
    featureFlags[name] = isSet;
    notifyListeners(name);
    return featureFlags[name];
  }

  function subscribe(featureFlag, callbackFn) {
    const newSubscription = {
      flag: featureFlag,
      callback: callbackFn,
    };

    _subscriptions.push(newSubscription);

    if (useHarness && harnessInstance) {
      harnessInstance.reapplyMappedKeysFromSplit();
    }

    isInitializedPromise.then(() => {
      notifyListeners(featureFlag);
    });

    return ((flag) => unsubscribe(flag));
  }

  return {
    initializeFeatureFlags,
    subscribe,
    get,
    getWhenReady,
    is,
    set,
    unsubscribe,
    getTreatmentConfig,
  };
})();

export { default as featureKeys } from './featureKeys';
